import { createElement as rc, Fragment } from 'react';
import PropTypes from 'prop-types';
import { View, ErrorBoundary, styled, fromTheme, webOnlyStyles } from 'lib_ui-primitives';
import SharedFilterBoundary from '../contextProviders/SharedFilterBoundary';

const Dashboard = styled(View).attrs({ name: 'dashboard-layout' })`
    flex-grow: 1;
    flex-shrink: 1;
    flex-basis: auto;
    width: 100%;
    flex-flow: row wrap;
    justify-content: center;
    align-content: ${({ compactLayout }) => (compactLayout ? 'flex-start' : 'center')};
    overflow-y: auto;
    gap: ${fromTheme('mainContainer', 'gap')};
    padding: ${fromTheme('mainContainer', 'padding')};
`;

let Tile = styled(View).attrs({ name: 'Tile' })`
    // A half-width card occupies one of four columns; a standard card occupies two.
    // Without an explicit half-width card, retain the existing automatic layout.
    flex: ${({ compactLayout, halfWidth }) => (compactLayout && halfWidth ? 0.5 : 1)} 1
        ${({ numWidgets, compactLayout, halfWidth, theme }) => {
            if (compactLayout && !theme.mobile) {
                const width = halfWidth ? 25 : 50;
                return `calc(${width}% - ${fromTheme('mainContainer', 'gap')({ theme })})`;
            }
            // mobile gets 1 widget per line, web gets 2 or 3 columns, depending on what looks better
            const numColumns = theme.mobile ? 1 : numWidgets % 3 === 0 ? 3 : 2;
            // we go a little smaller, and it will grow as needed to fill the space
            return `${100 / (numColumns + 1)}%`;
        }};

    min-width: ${({ compactLayout, halfWidth }) => (compactLayout ? `min(100%, ${halfWidth ? 200 : 400}px)` : '0')};
    // Leave room for the chart header, padding and total; shorter tiles clip the count.
    min-height: ${({ compactLayout }) => (compactLayout ? '260px' : '0')};
    height: ${({ numWidgets, compactLayout, numRows: configuredRows, size, theme }) => {
        if (theme.mobile) {
            return 'auto';
        }
        const numColumns = numWidgets % 3 === 0 ? 3 : 2;
        const numRows = compactLayout ? configuredRows : Math.ceil(numWidgets / numColumns);
        const modifier = size === 'tall' ? 10 : size === 'short' ? -10 : 0;
        // Divide the available height by rows, then apply the configured size.
        return `${100 / numRows - 1 + modifier}%`;
    }};
    max-height: ${({ numWidgets, compactLayout, numRows: configuredRows, size, theme }) => {
        if (theme.mobile) {
            return 'none';
        }
        const numColumns = numWidgets % 3 === 0 ? 3 : 2;
        const numRows = compactLayout ? configuredRows : Math.ceil(numWidgets / numColumns);
        const modifier = size === 'tall' ? 10 : size === 'short' ? -10 : 0;
        return `${100 / numRows - 1 + modifier}%`;
    }};
    overflow: hidden;
`;
Tile = webOnlyStyles(Tile)`
   
`;

function DashboardLayout(props) {
    const { id, title: name = 'dashboard', children, ...otherProps } = props || {};

    const compactLayout = children.some(child => child.props?.hNode?.chartWidth === 'half');
    const numRows = Math.ceil(
        children.reduce((width, child) => width + (child.props?.hNode?.chartWidth === 'half' ? 0.5 : 1), 0) / 2
    );

    // prettier-ignore
    return rc(Dashboard, { name, id, compactLayout, ...otherProps },
        children.map((child, i) =>
            rc(Tile, { key: `widget${i}`, numWidgets: children.length, compactLayout, numRows, halfWidth: child.props?.hNode?.chartWidth === 'half', size: child.props?.hNode?.chartHeight || 'medium' },
                // Charts with filterRelation use the page filters; other widgets keep their own filters.
                rc(child.props?.hNode?.filterRelation ? Fragment : SharedFilterBoundary, child.props?.hNode?.filterRelation ? null : { boundaryName: child.props?.hNode?.title },
                    rc(ErrorBoundary, null,
                        child
                    )
                )
            )
        )
    );
}

DashboardLayout.propTypes = {
    children: PropTypes.array,
    hNode: PropTypes.object.isRequired
};

export default DashboardLayout;
